import React, { FC, memo, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { getFleetsByCurrentSemanticMap } from "Slices/fleets";
import { fetchDeployMissionPlan } from "Slices/missionPlans";
import {
  getIsPickedFleetHasVehicles,
  getMissionOperatorPickedFleetId,
  getOperatorMissionStatus,
} from "Slices/operatorData";
import {
  getCurrentMissionAsset,
  getIsMissionAssetsEmpty,
} from "Slices/operatorMissionAssets";
import {
  getIsMissionAssetsLoaded,
  getIsMissionAssetsLoadFailed,
} from "Slices/operatorMissionAssets/selectors";
import { getOperatorPickedStation } from "Slices/operatorStation";

import { FMSLogger } from "FMSLogger";
import { MISSION_OPERATOR_ACTIONS } from "Utils/constants";
import { storageKeys } from "Utils/localStorageUtils";

import { ReactComponent as HornIcon } from "../../assets/horn-icon.svg";
import { ReactComponent as MapIcon } from "../../assets/map-icon.svg";

import {
  InteractionPaneWrapper,
  CallVehicleButtonStyled,
  EmptyErrorStyled,
  MapViewStyled,
} from "./style";

const missionOperatorLogger = FMSLogger.byPrefix(MISSION_OPERATOR_ACTIONS);

type StationActionsProps = {
  initFleetId: string;
  isPickerDisabled?: boolean;
  shouldUseSavedFleet: boolean;
  handleSelectedFleet: (id: string | null) => void;
};

export const StationActions: FC<StationActionsProps> = memo(
  ({
    initFleetId,
    isPickerDisabled,
    shouldUseSavedFleet,
    handleSelectedFleet,
  }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const pickedStation = useSelector(getOperatorPickedStation);
    const currentMissionAsset = useSelector(getCurrentMissionAsset);
    const isMissionAssetsLoaded = useSelector(getIsMissionAssetsLoaded);
    const isMissionAssetsLoadFailed = useSelector(getIsMissionAssetsLoadFailed);
    const isMissionAssetsEmpty = useSelector(getIsMissionAssetsEmpty);
    const missionOperatorPickedFleetId = useSelector(
      getMissionOperatorPickedFleetId
    );
    const isPickedFleetHasVehicles = useSelector(getIsPickedFleetHasVehicles);
    const fleets = useSelector(getFleetsByCurrentSemanticMap);
    const operatorMissionStatus = useSelector(getOperatorMissionStatus);

    useEffect(() => {
      if (fleets.length === 0) {
        handleSelectedFleet(null);
        return;
      }

      const findFleetById = (fleetId: string | null) =>
        fleets.find((fleet) => fleet.id === fleetId);

      const firstFleet = fleets[0];
      let selectedFleet = firstFleet;

      if (initFleetId) {
        selectedFleet = findFleetById(initFleetId) ?? firstFleet;
      } else if (shouldUseSavedFleet) {
        selectedFleet =
          findFleetById(
            localStorage.getItem(storageKeys.MISSION_OPERATOR_SAVED_FLEET_ID)
          ) ?? firstFleet;
      }
      handleSelectedFleet(selectedFleet.id);
    }, [fleets]);

    const handleDeployMissionAsset = useCallback(() => {
      missionOperatorLogger.debug("Call Vehicle button pressed");
      if (!currentMissionAsset || !missionOperatorPickedFleetId) {
        missionOperatorLogger.error(
          "No currentMissionAsset or missionOperatorPickedFleetId when Call Vehicle button pressed"
        );
        return;
      }

      if (operatorMissionStatus !== "NO_MISSION") {
        missionOperatorLogger.error(
          "Cannot call vehicle while another mission is in progress"
        );
        return;
      }

      dispatch(
        fetchDeployMissionPlan({
          ...currentMissionAsset,
          fleet: missionOperatorPickedFleetId,
          stationId: pickedStation?.stationId,
        })
      );
    }, [
      currentMissionAsset,
      missionOperatorPickedFleetId,
      pickedStation,
      operatorMissionStatus,
    ]);

    const isCallVehicleButtonDisabled =
      !currentMissionAsset ||
      !missionOperatorPickedFleetId ||
      isPickerDisabled ||
      !isPickedFleetHasVehicles ||
      operatorMissionStatus !== "NO_MISSION";

    const ErrorMessage = useMemo(() => {
      if (isMissionAssetsEmpty || isMissionAssetsLoadFailed)
        return <EmptyErrorStyled>Error: no mission plans!</EmptyErrorStyled>;

      if (!currentMissionAsset && isMissionAssetsLoaded)
        return (
          <EmptyErrorStyled>
            Error: No appropriate mission plan!
          </EmptyErrorStyled>
        );
      return null;
    }, [
      isMissionAssetsEmpty,
      currentMissionAsset,
      isMissionAssetsLoaded,
      isMissionAssetsLoadFailed,
    ]);

    return (
      <InteractionPaneWrapper>
        <CallVehicleButtonStyled
          disabled={isCallVehicleButtonDisabled}
          onClick={handleDeployMissionAsset}
        >
          Call Vehicle
          <HornIcon />
        </CallVehicleButtonStyled>
        {ErrorMessage}
        <MapViewStyled
          onClick={() => {
            navigate("/");
          }}
        >
          Map view
          <MapIcon />
        </MapViewStyled>
      </InteractionPaneWrapper>
    );
  }
);

export default StationActions;
